import { useState, useRef, useEffect, useCallback } from "react";
import { MONTH_NAMES } from "../../../shared/types/date-helpers";

interface MonthYearPickerProps {
  readonly currentDate: string;
  readonly onGoToDate: (date: string) => void;
  readonly label: string;
  readonly className?: string;
}

function parseDate(dateStr: string) {
  const d = new Date(dateStr + "T00:00:00");
  return { month: d.getMonth(), year: d.getFullYear() };
}

export function MonthYearPicker({
  currentDate,
  onGoToDate,
  label,
  className,
}: MonthYearPickerProps) {
  const [open, setOpen] = useState(false);
  const { month, year } = parseDate(currentDate);
  const [pickerYear, setPickerYear] = useState(year);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setPickerYear(year);
  }, [year]);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        close();
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open, close]);

  function handleSelect(m: number) {
    const mm = String(m + 1).padStart(2, "0");
    onGoToDate(`${pickerYear}-${mm}-01`);
    setOpen(false);
  }

  return (
    <div className="relative" ref={popoverRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={className}
      >
        {label}
      </button>

      {open && (
        <div
          className="absolute top-full left-1/2 -translate-x-1/2 mt-2 z-50
            bg-surface border border-border/60 rounded-xl shadow-xl p-3 w-52"
        >
          <div className="flex items-center justify-between mb-3">
            <button
              type="button"
              onClick={() => setPickerYear((y) => y - 1)}
              className="min-h-8 min-w-8 flex items-center justify-center
                text-ink-muted hover:text-ink rounded-lg hover:bg-surface-alt"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className="text-sm font-bold text-ink">{pickerYear}</span>
            <button
              type="button"
              onClick={() => setPickerYear((y) => y + 1)}
              className="min-h-8 min-w-8 flex items-center justify-center
                text-ink-muted hover:text-ink rounded-lg hover:bg-surface-alt"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-3 gap-1">
            {MONTH_NAMES.map((name, i) => {
              const isActive = i === month && pickerYear === year;
              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => handleSelect(i)}
                  className={`px-1 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                    isActive
                      ? "bg-accent text-white"
                      : "text-ink-muted hover:text-ink hover:bg-surface-alt"
                  }`}
                >
                  {name.slice(0, 3)}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
